import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Modal,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import "../../node_modules/react-vis/dist/style.css";
import "../css/home.css";
import StockRadarChart from "./StockRadarChart";
import Utils from "../util/Utils";
import CompanyPage from "../company/CompanyPage";

// Design tokens (same as CompanyProfile)
const C = {
  textPrimary: "#0f1f35",
  textSecondary: "#3d5166",
  cardHeader: "#f5f2ed",
  cardHeaderText: "#1a2744",
  border: "#d0dcea",
  borderLight: "#e8eef6",
};
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

// Peer card – same base as profile StyledCard
const PeerCard = styled(Card)(() => ({
  fontFamily: SANS,
  height: "100%",
  width: "100%",
  maxWidth: 280,
  margin: "0 auto",
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
  overflow: "hidden",
  transition: "box-shadow 0.15s",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(15,45,82,0.1)",
  },
  "& .MuiCardContent-root": {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
}));

// Compact table container (profile-style cell padding)
const PeerTableContainer = styled(TableContainer)(({ theme }) => ({
  fontFamily: SANS,
  "& .MuiTableCell-root": {
    padding: theme.spacing(0.35, 1.25),
    fontSize: "0.875rem",
    lineHeight: 1.25,
    fontFamily: SANS,
    borderColor: C.borderLight,
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: C.cardHeader,
  },
}));

function SectionHeaderRow({ title }) {
  return (
    <TableRow sx={{ backgroundColor: C.cardHeader }}>
      <TableCell
        colSpan={2}
        sx={{
          fontFamily: SANS,
          backgroundColor: C.cardHeader,
          color: C.cardHeaderText,
          fontWeight: 700,
          fontSize: "0.875rem",
          py: 0.5,
          px: 1.5,
          borderTop: `1px solid ${C.borderLight}`,
          borderBottom: `1px solid ${C.borderLight}`,
        }}
      >
        {title}
      </TableCell>
    </TableRow>
  );
}

function DataRow({ label, value }) {
  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{
          fontWeight: 600,
          fontSize: "0.875rem",
          color: C.textSecondary,
          fontFamily: SANS,
          px: 1.5,
        }}
      >
        {label}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: "0.875rem",
          fontWeight: 500,
          color: C.textPrimary,
          fontFamily: SANS,
          px: 1.5,
        }}
      >
        {value}
      </TableCell>
    </TableRow>
  );
}

export default function CompareResults({ stckLst }) {
  const [stateValues, setstateValues] = useState({ symbol: "", open: false });
  const [showAllDetails, setShowAllDetails] = useState(false);

  const toggleAllDetails = () => setShowAllDetails((prev) => !prev);
  const showModal = (symbol) => setstateValues({ symbol, open: true });
  const closeModal = () => setstateValues((prev) => ({ ...prev, open: false }));

  return (
    <Box
      className="compare-to-peers-page"
      sx={{
        maxWidth: 1280,
        margin: "0 auto",
        py: 2,
        px: { xs: 1, sm: 1.5, md: 2 },
        fontFamily: SANS,
        color: C.textPrimary,
      }}
    >
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Button
          size="medium"
          variant="contained"
          color={showAllDetails ? "error" : "primary"}
          onClick={toggleAllDetails}
        >
          {showAllDetails ? "Show Less Details" : "Show All Details"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
        }}
      >
        {stckLst.slice(0, 4).map((stock) => (
          <Box
            key={stock.symbol}
            sx={{
              flex: "1 1 260px",
              minWidth: 260,
              maxWidth: 280,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <PeerCard onClick={() => showModal(stock.symbol)} elevation={0}>
              {/* Header: cream bar with logo, symbol, price */}
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  borderBottom: `1px solid ${C.borderLight}`,
                  backgroundColor: C.cardHeader,
                }}
              >
                <Box
                  component="img"
                  src={stock.image}
                  alt=""
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    objectFit: "contain",
                  }}
                />
                <Box sx={{ flex: 1, textAlign: "right" }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: C.cardHeaderText,
                      fontFamily: SANS,
                    }}
                  >
                    {stock.symbol}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "1rem",
                      color: C.textSecondary,
                      fontFamily: SANS,
                    }}
                  >
                    ${stock.price}
                  </Typography>
                </Box>
              </CardContent>

              {/* Radar chart */}
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 130,
                  py: 0.5,
                }}
              >
                <StockRadarChart
                  darkBackground={false}
                  scoreData={stock}
                  width={160}
                  height={120}
                  labelSize="short"
                />
              </CardContent>

              {/* Optional detail tables */}
              {showAllDetails && (
                <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                  <PeerTableContainer component={Paper} elevation={0}>
                    <Table size="small" stickyHeader={false}>
                      <TableBody>
                        <SectionHeaderRow title="Performance" />
                        <DataRow label="1 Day Performance" value={Utils.format_ratio(stock.perfDay, 0, 1)} />
                        <DataRow label="1 Week Performance" value={Utils.format_ratio(stock.perfWeek, 0, 1)} />
                        <DataRow label="1 Month Performance" value={Utils.format_ratio(stock.perfMonth, 0, 1)} />
                        <DataRow label="1 Year Performance" value={Utils.format_ratio(stock.perfYear, 0, 1)} />
                        <DataRow label="YTD Performance" value={Utils.format_ratio(stock.perfYTD, 0, 1)} />
                        <DataRow label="5Y (Annualized)" value={Utils.format_ratio(stock.perf5Year, 0, 1)} />
                        <DataRow label="5Y (Actual return)" value={Utils.format_ratio(stock.perf5YearTotal, 0, 1)} />
                        <DataRow label="10Y (Annualized)" value={Utils.format_ratio(stock.perf10Year, 0, 1)} />
                        <DataRow label="10Y (Actual return)" value={Utils.format_ratio(stock.perf10YearTotal, 0, 1)} />

                        <SectionHeaderRow title="Financials" />
                        <DataRow label="Market Cap" value={Utils.format_currency_short(stock.marketCap)} />
                        <DataRow label="Price" value={Utils.format_currency_usd(stock.price)} />
                        <DataRow label="Fair Value" value={Utils.format_currency_usd(stock.intrinsicValue)} />
                        <DataRow label="Dividend Yield" value={Utils.format_ratio(stock.dividendYield, 4, 0)} />
                        <DataRow label="PE" value={Utils.format_none(stock.pe, 15, 0)} />
                        <DataRow label="PEG" value={Utils.format_number(stock.pegRatio, 1.5, 0)} />
                        <DataRow label="ROE" value={Utils.format_none(stock.roe, 12, 1) + "%"} />
                        <DataRow label="Gross Margin" value={Utils.format_ratio(stock.grossMargin, 50, 1) + "%"} />
                        <DataRow label="Profit Margin" value={Utils.format_ratio(stock.profitMargin, 25, 1).replace("%%", "%")} />
                        <DataRow label="Debt to Equity" value={Utils.format_none(stock.debtEq, 1, 0)} />

                        <SectionHeaderRow title="Valuation" />
                        <DataRow label="Price to Earnings (P/E)" value={Utils.format_none(stock.pe, 15, 0)} />
                        <DataRow label="Price to Earnings Growth (PEG)" value={Utils.format_number(stock.pegRatio, 1.5, 0)} />
                      </TableBody>
                    </Table>
                  </PeerTableContainer>
                </CardContent>
              )}
            </PeerCard>
          </Box>
        ))}
      </Box>

      <Modal open={stateValues.open} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            maxWidth: 900,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 0,
          }}
        >
          <Box sx={{ p: 2 }}>
            <CompanyPage symbol={stateValues.symbol} />
          </Box>
          <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="primary" onClick={closeModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
